import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Avatar, Typography } from '@mui/material';
import styled from 'styled-components';
import { THEME_COLORS } from '../../../common/colors';
import { TextFormField } from '../../../common/Form/TextFormField';
import { PhoneNumberInput } from '../../../common/PhoneNumberInput';
import { MinimalLink } from '../../../profile/styledComponents';
import { Body10, Header12, Header20 } from '../../../common/Typography';
import Lottie from 'react-lottie';
import Success from '../../../../assets/lotties/success.json';
import { DialogContent } from '@material-ui/core';
import DialogForm from '../../../common/DialogForm';
import useCreateOffer from './useCreateOffer';
import MenuItem from '@mui/material/MenuItem';
import { AuthContext } from '../../../app/AuthContext';
import { useFetchUserAssets } from './useFetchUserAssets';
import PrimaryButton from '../../../common/PrimaryButton';
import { useHistory } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import { styled as styledMUI } from '@mui/material/styles';

const UserDetails = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: start;
`;

const UserDescription = styled.div`
  margin-left: 12px;
  color: #717273;
  display: flex;
`;

const DescriptionPlaceholder = styled.div`
  position: absolute;
  padding: 0.6rem 0.8rem;
  top: 0;
  &:hover {
    cursor: pointer;
  }
  span {
    color: rgba(0, 0, 0, 0.3) !important;
    display: block;
  }
`;

const PhoneInput = styled(PhoneNumberInput)`
  img {
    position: relative;
    top: 4px;
  }

  .MuiFormHelperText-filled {
    margin-left: 0 !important;
  }
`;

const LoginOrCreateAssetContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 32px 0;
  width: 100%;
  button {
    margin-bottom: 16px;
  }
`;

const FormField = styled(TextFormField)`
  z-index: 999;
  .MuiInputBase-root {
    margin-bottom: 0.1rem !important;
  }
  .Mui-error {
    margin-left: 0 !important;
  }
  .MuiInputBase-input {
    padding: 8px !important;
  }
`;

const FieldHeader = styled(Typography).attrs({
  component: 'h3',
})`
  margin: 1rem 0 0.6rem 0 !important;
`;

const Subheader = styled(Body10)`
  margin-top: -4px !important;
  margin-bottom: 0.6rem !important;
  font-style: italic !important;
`;

const TextAreaLimit = styled.div`
  &,
  span {
    color: ${THEME_COLORS.primary} !important;
  }
  text-align: right;
  font-size: 0.8rem;
`;

const Select = styledMUI(TextField)(() => ({
  width: '100%',
  borderRadius: 0,
  marginBottom: 24,
  '.MuiInputBase-root::before': {
    border: 'unset !important',
  },
  '.MuiSelect-select': {
    padding: '12px',
  },
  '.Mui-error': {
    marginLeft: '0 !important',
  },
  '.MuiInputBase-root::after': {
    border: 'unset !important',
  },
  '.MuiInputBase-multiline': {
    borderRadius: 0,
    padding: '2px',
  },
  '.MuiInputBase-root': {
    borderRadius: 0,
  },
}));

function generateInitialState(asset) {
  return {
    offerDetails: '',
    name: '',
    phone: '',
    pronouns: '',
    asset: {
      url: `https://app.wesuap.com/assets/${asset?.uuid}`,
      name: `${asset?.user.firstName} ${asset?.user.lastName}`,
      userUuid: asset?.user?.uuid,
      title: asset?.title,
    },
  };
}

function LoginOrCreateAsset({ notLoggedIn, missingAssets, history }) {
  return (
    <LoginOrCreateAssetContent>
      {notLoggedIn && (
        <>
          <Header20 sx={{ textAlign: 'center' }}>
            Sign up or log in to chat about this offer!
          </Header20>
          <ActionSection>
            <PrimaryButton onClick={() => history.push('/signup')}>
              Sign up
            </PrimaryButton>
            Have an account already?{' '}
            <MinimalLink href="/login">Log in</MinimalLink>
          </ActionSection>
        </>
      )}
      {!notLoggedIn && missingAssets && (
        <>
          <Header20 sx={{ textAlign: 'center' }}>
            Create an offer to start chatting!
          </Header20>
          <ActionSection>
            <PrimaryButton onClick={() => history.push('/my-offerings')}>
              Got to my offerings
            </PrimaryButton>
          </ActionSection>
        </>
      )}
    </LoginOrCreateAssetContent>
  );
}

function OfferForm({
  user,
  swapData,
  setSwapData,
  isSubmitted,
  asset,
  errors,
  isMobile,
}) {
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const { userAssets } = useFetchUserAssets(user);

  useEffect(() => {
    setSwapData({
      ...swapData,
      name: user.firstname,
      phone: user.phone,
      pronouns: user.pronouns,
    });
  }, [user]);

  const onFieldChange = formData => {
    setSwapData({
      ...swapData,
      ...formData,
    });
  };

  const onValueChange = useCallback(
    field => value => {
      onFieldChange({ [field]: value });
    },
    [swapData, onFieldChange],
  );

  const onEventChange = useCallback(
    field => e => {
      const value = e.target.value;
      onValueChange(field)(value);
    },
    [swapData, onValueChange],
  );

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: Success,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DialogContent>
      {isSubmitted && (
        <div>
          <Lottie options={defaultOptions} width="100%" speed={0.4} />
          <Header20 sx={{ textAlign: 'center' }}>
            Bet! We'll send you a message via text once {asset.user.firstName}{' '}
            replies back!
          </Header20>
        </div>
      )}
      {!isSubmitted && (
        <>
          <UserDetails>
            <Avatar
              alt="user-profile-picture"
              src={asset.user.profilePicture}
            />
            <UserDescription>
              <MinimalLink href={`/profile/${asset.user.uuid}`}>
                <Typography variant="body1">
                  You’re requesting from {asset.user.firstName}
                </Typography>
                <Header12>{asset.title}</Header12>
              </MinimalLink>
            </UserDescription>
          </UserDetails>
          <FieldHeader>What can you offer in return?*</FieldHeader>
          <Select
            id="filled-select-asset"
            select
            error={!!errors.asset}
            helperText={errors.asset}
            value={(selectedAsset ?? {}).id ?? 0}
            variant="outlined"
            color="warning"
            style={{ width: '100%' }}
            onChange={e => {
              const value = e.target.value;
              const chosenAsset =
                userAssets.find(asset => asset.id === value) ?? null;
              setSelectedAsset(chosenAsset);

              if (chosenAsset) {
                onFieldChange({
                  offerDetails: `Offer: ${chosenAsset.title} • Description: ${chosenAsset.description} • View my offer: ${process.env.REACT_APP_PUBLIC_URL}/assets/${chosenAsset.uuid}
              `,
                });
              } else {
                onFieldChange({
                  offerDetails: '',
                });
              }
            }}
          >
            <MenuItem key={`asset-placeholder`} value={0}>
              Select an offering...
            </MenuItem>
            {userAssets.map(option => (
              <MenuItem
                key={`asset-${option.title}-${option.id}`}
                value={option.id}
              >
                {option.title}
              </MenuItem>
            ))}
          </Select>
          <Subheader>
            Remember, your skills can be ANYTHING. Think about what you’re
            capable of, what you’ve spent time learning/accomplishing, things
            you love or have a gift for!
          </Subheader>
          <div style={{ position: 'relative' }}>
            <FormField
              color="warning"
              id="field-offer"
              multiline
              error={!!errors.offerDetails}
              helperText={errors.offerDetails}
              value={swapData.offerDetails}
              placeholder=""
              rows={4}
              onChange={e => {
                setShowPlaceholder(!e.target.value);
                onEventChange('offerDetails')(e);
              }}
            />
            {!swapData.offerDetails && showPlaceholder && (
              <DescriptionPlaceholder>
                <span>- 1:1 brief, solution focused therapeutic support</span>
                <span>
                  - creative direction, specific to movement and music based
                  projects
                </span>
                <span>- early career mentorship</span>
              </DescriptionPlaceholder>
            )}
          </div>
          <TextAreaLimit>800 characters or less</TextAreaLimit>
          <FieldHeader>Name*</FieldHeader>
          <FormField
            id="field-name"
            error={!!errors.name}
            helperText={errors.name}
            value={swapData.name}
            color="warning"
            placeholder="Name"
            onChange={onEventChange('name')}
          />
          <FieldHeader>Pronouns</FieldHeader>
          <FormField
            id="field-pronouns"
            value={swapData.pronouns}
            color="warning"
            placeholder="they/them (optional)"
            onChange={onEventChange('pronouns')}
          />
          <FieldHeader>Phone number*</FieldHeader>
          <PhoneInput
            color="warning"
            isMobile={isMobile}
            id="form-phone-number"
            error={!!errors.phone}
            helperText={
              errors.phone ||
              "We'll text you when you’re matched! No spam, just support."
            }
            onChange={phone => {
              onFieldChange({
                phone,
              });
            }}
            value={swapData.phone}
          />
        </>
      )}
    </DialogContent>
  );
}

export function SuapFormLite({ user, asset, isMobile, onCancel, isOpen }) {
  const history = useHistory();
  const { authContext, isProfileActive } = useContext(AuthContext);
  const notLoggedIn = !authContext.token;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { loading: saving, createSwap, errors } = useCreateOffer(
    asset.uuid,
    setIsSubmitted,
  );
  const [swapData, setSwapData] = useState(generateInitialState(asset));

  useEffect(() => {
    setSwapData(generateInitialState(asset));
  }, [asset]);

  const handleCancel = useCallback(() => {
    onCancel();
    if (isSubmitted) {
      setIsSubmitted(false);
      setSwapData(generateInitialState(asset));
    }
  }, [isSubmitted]);
  const handleSave = useCallback(() => {
    createSwap(swapData);
  }, [swapData]);

  return (
    <DialogForm
      label={`Send a message to ${asset.user.firstName}`}
      description=""
      isOpen={isOpen}
      width={640}
      buttonLabel="Submit"
      isMobile={isMobile}
      hideCloseButton={true}
      closeLabel="Close"
      loading={saving}
      onSave={!isSubmitted && isProfileActive ? handleSave : undefined}
      onCancel={isSubmitted ? handleCancel : undefined}
      onClose={handleCancel}
    >
      <DialogContent>
        {(notLoggedIn || !isProfileActive) && (
          <LoginOrCreateAsset
            notLoggedIn={notLoggedIn}
            missingAssets={!isProfileActive}
            history={history}
          />
        )}
        {!!isProfileActive && (
          <OfferForm
            asset={asset}
            isSubmitted={isSubmitted}
            errors={errors}
            setSwapData={setSwapData}
            swapData={swapData}
            user={user}
            isMobile={isMobile}
          />
        )}
      </DialogContent>
    </DialogForm>
  );
}
